import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, Users, BookmarkSimple, Certificate, PencilSimple } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [stats, setStats] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [editedContent, setEditedContent] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedToken = localStorage.getItem('powr-admin-token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
      fetchAdminData(savedToken);
    }
  }, []);

  const fetchAdminData = async (authToken) => {
    try {
      const [statsRes, modulesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${authToken}` }
        }),
        axios.get(`${API}/modules`)
      ]);
      setStats(statsRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });
      const newToken = response.data.access_token;
      setToken(newToken);
      localStorage.setItem('powr-admin-token', newToken);
      setIsAuthenticated(true);
      toast.success('Welcome, Admin');
      fetchAdminData(newToken);
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken('');
    localStorage.removeItem('powr-admin-token');
    navigate('/');
  };

  const handleEditModule = (module) => {
    setSelectedModule(module);
    setEditedContent({
      title: module.title,
      identity_statement: module.content.identity_statement,
      core_teaching: module.content.core_teaching,
      key_principles: module.content.key_principles.join('\n'),
      food_for_thought: module.content.food_for_thought.join('\n'),
      real_life_application: module.content.real_life_application,
      closing_charge: module.content.closing_charge
    });
  };

  const handleSaveModule = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/modules/${selectedModule.slug}`,
        {
          title: editedContent.title,
          content: {
            identity_statement: editedContent.identity_statement,
            core_teaching: editedContent.core_teaching,
            key_principles: editedContent.key_principles.split('\n').filter(p => p.trim()),
            food_for_thought: editedContent.food_for_thought.split('\n').filter(q => q.trim()),
            real_life_application: editedContent.real_life_application,
            closing_charge: editedContent.closing_charge
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Module updated successfully');
      setSelectedModule(null);
      fetchAdminData(token);
    } catch (error) {
      toast.error('Failed to update module');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <Card className="p-8 bg-[hsl(var(--card))] rounded-none border border-[hsl(var(--border))]">
            <div className="text-center mb-8">
              <Lock size={48} weight="duotone" className="mx-auto mb-4 text-[hsl(var(--accent))]" />
              <h1 className="text-3xl font-black mb-2">Admin Access</h1>
              <p className="text-sm text-[hsl(var(--text-muted))]">Enter your credentials to continue</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  data-testid="admin-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="rounded-none mt-2"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-none mt-2"
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="admin-login-btn"
                disabled={loading}
                className="w-full rounded-none bg-[hsl(var(--primary))] hover:bg-[hsl(var(--accent))] transition-all"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
            <p className="mt-4 text-xs text-center text-[hsl(var(--text-muted))]">
              Default: admin / powr2024
            </p>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6 md:px-12">
      <div className="container mx-auto max-w-6xl">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="text-4xl font-black mb-2">Admin Dashboard</h1>
            <p className="text-[hsl(var(--text-muted))]">Manage POWR course content</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            variant="outline"
            className="rounded-none border-[hsl(var(--border))]"
          >
            Logout
          </Button>
        </div>

        <Tabs defaultValue="stats" className="space-y-8">
          <TabsList className="bg-[hsl(var(--muted))] rounded-none p-1">
            <TabsTrigger value="stats" data-testid="tab-stats" className="rounded-none">
              <Eye size={16} className="mr-2" />
              Stats
            </TabsTrigger>
            <TabsTrigger value="modules" data-testid="tab-modules" className="rounded-none">
              <PencilSimple size={16} className="mr-2" />
              Edit Modules
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stats" className="space-y-6">
            {stats && (
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="p-6 bg-[hsl(var(--card))] rounded-none border border-[hsl(var(--border))]">
                  <Users size={32} weight="duotone" className="text-[hsl(var(--accent))] mb-4" />
                  <p className="text-3xl font-black mb-2">{stats.total_users}</p>
                  <p className="text-sm text-[hsl(var(--text-muted))]">Total Users</p>
                </Card>
                <Card className="p-6 bg-[hsl(var(--card))] rounded-none border border-[hsl(var(--border))]">
                  <BookmarkSimple size={32} weight="duotone" className="text-[hsl(var(--accent))] mb-4" />
                  <p className="text-3xl font-black mb-2">{stats.total_bookmarks}</p>
                  <p className="text-sm text-[hsl(var(--text-muted))]">Total Bookmarks</p>
                </Card>
                <Card className="p-6 bg-[hsl(var(--card))] rounded-none border border-[hsl(var(--border))]">
                  <Certificate size={32} weight="duotone" className="text-[hsl(var(--accent))] mb-4" />
                  <p className="text-3xl font-black mb-2">{stats.total_certificates}</p>
                  <p className="text-sm text-[hsl(var(--text-muted))]">Certificates Issued</p>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="modules" className="space-y-6">
            {!selectedModule ? (
              <div className="grid md:grid-cols-2 gap-6">
                {modules.map((module) => (
                  <Card key={module.slug} className="p-6 bg-[hsl(var(--card))] rounded-none border border-[hsl(var(--border))]">
                    <h3 className="text-2xl font-black mb-2">{module.title}</h3>
                    <p className="text-sm text-[hsl(var(--text-muted))] mb-4">{module.content.identity_statement}</p>
                    <Button
                      onClick={() => handleEditModule(module)}
                      data-testid={`edit-module-${module.slug}`}
                      className="rounded-none bg-[hsl(var(--accent))] hover:bg-[hsl(var(--secondary))]"
                    >
                      <PencilSimple size={16} className="mr-2" />
                      Edit Module
                    </Button>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-black">Editing: {selectedModule.title}</h2>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedModule(null)}
                    data-testid="cancel-edit-btn"
                    className="rounded-none"
                  >
                    Cancel
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Module Title</Label>
                    <Input
                      id="title"
                      value={editedContent.title}
                      onChange={(e) => setEditedContent({ ...editedContent, title: e.target.value })}
                      className="rounded-none mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="identity">Identity Statement</Label>
                    <Textarea
                      id="identity"
                      value={editedContent.identity_statement}
                      onChange={(e) => setEditedContent({ ...editedContent, identity_statement: e.target.value })}
                      className="rounded-none mt-2 min-h-[80px]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="teaching">Core Teaching</Label>
                    <Textarea
                      id="teaching"
                      value={editedContent.core_teaching}
                      onChange={(e) => setEditedContent({ ...editedContent, core_teaching: e.target.value })}
                      className="rounded-none mt-2 min-h-[300px]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="principles">Key Principles (one per line)</Label>
                    <Textarea
                      id="principles"
                      value={editedContent.key_principles}
                      onChange={(e) => setEditedContent({ ...editedContent, key_principles: e.target.value })}
                      className="rounded-none mt-2 min-h-[150px]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="thought">Food for Thought (one per line)</Label>
                    <Textarea
                      id="thought"
                      value={editedContent.food_for_thought}
                      onChange={(e) => setEditedContent({ ...editedContent, food_for_thought: e.target.value })}
                      className="rounded-none mt-2 min-h-[150px]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="application">Real Life Application</Label>
                    <Textarea
                      id="application"
                      value={editedContent.real_life_application}
                      onChange={(e) => setEditedContent({ ...editedContent, real_life_application: e.target.value })}
                      className="rounded-none mt-2 min-h-[200px]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="closing">Closing Charge</Label>
                    <Textarea
                      id="closing"
                      value={editedContent.closing_charge}
                      onChange={(e) => setEditedContent({ ...editedContent, closing_charge: e.target.value })}
                      className="rounded-none mt-2 min-h-[100px]"
                    />
                  </div>

                  <Button
                    onClick={handleSaveModule}
                    data-testid="save-module-btn"
                    disabled={loading}
                    className="w-full rounded-none bg-[hsl(var(--primary))] hover:bg-[hsl(var(--accent))] transition-all"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;